// client/src/views/InteractiveMap.js
import React, { useRef, useState, useEffect } from 'react';
import { MapContainer, ImageOverlay, Marker, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import '@geoman-io/leaflet-geoman-free';
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css';
import POILayer from './Interactive_parts/POILayer';
import 'leaflet/dist/leaflet.css';
import { personalPinIcon } from './Interactive_parts/PinUtils.js';
import EmbedPopup from './Interactive_parts/EmbedPopup.js';
import MouseCoords from './Interactive_parts/MouseCoords.js';
import SetZoomControlPosition from './Interactive_parts/SetZoomControlPosition.js';
import ClickHandler from './Interactive_parts/ClickHandler.js';
import CoordPopup from './Interactive_parts/CoordPopup.js';
import PersonalPinModal from './Interactive_parts/PersonalPinModal.js';
import isMobileScreen from './Interactive_parts/isMobileScreen.js';
import MapClickPopupCloser from './Interactive_parts/MapClickPopupCloser.js';
import { POISelectedIdContext } from './Interactive_parts/POISelectedIdContext.js';
import ShapeEditToolbar from './Interactive_parts/ShapeEditToolbar';
import POICreationPopup from './Interactive_parts/POICreationPopup.js';
import {
  apiBase,
  useAnyModalOpen,
  resolveUploadsUrl,
  ShapePathingRunner,
  GeomanControls,
} from './Interactive_parts/InteractiveMapInternals.js';
import {
  useZoneHoverGlow,
  useMapCreateGuard,
  usePersonalPinsStorage,
} from './Interactive_parts/InteractiveMapEffects.js';
import {
  assetToBounds,
  useOverlayEditing,
  OverlayEditHUD,
} from './Interactive_parts/OverlayEditing.js';
import MapControls from './Interactive_parts/MapControls.js';
import { createShapeHandlers } from './Interactive_parts/shapeHandlers.js';
import { createPersonalPinHandlers } from './Interactive_parts/personalPinHandlers.js';
import { createPoiCreationHandlers } from './Interactive_parts/poiCreationHandlers.js';
import { createViewHandlers } from './Interactive_parts/viewUtils.js';
import OverlayAssetModal from './Interactive_parts/OverlayAssetModal.js';
import AdSlot from '../components/AdSlot';

function FitToImage({ imageSize, mapId, resetTrigger }) {
  const map = useMap();

  useEffect(() => {
    if (!imageSize || !Array.isArray(imageSize)) return;
    const bounds = [[0, 0], imageSize];
    try {
      map.fitBounds(bounds, {
        animate: false,
        padding: [0, 0],
      });
    } catch (e) {
    }
  }, [map, imageSize, mapId, resetTrigger]);

  return null;
}

function AllowLoosePanning() {
  const map = useMap();

  useEffect(() => {
    if (map.setMaxBounds) {
      map.setMaxBounds(null);
    }
    if (map.options) {
      map.options.maxBoundsViscosity = 0;
    }
  }, [map]);

  return null;
}

function InteractiveMap(props) {
  const {
    mapData,
    imageSize,
    pois,
    foundPOIs,
    showFound,
    setShowFound,
    visibleTypes,
    visibleCategories,
    hoveredType,
    hoveredPoiId,
    setHoveredPoiId,
    pendingPOI,
    setPendingPOI,
    isMapOwnerOrFounder,
    handleMapCoordClick,
    handleClick,
    mouseCoord,
    handleMouseCoord,
    shareCoord,
    handleCopyCoord,
    copiedCoord,
    customCategories,
    typeColours,
    customTypes,
    selectedCategory,
    setSelectedCategory,
    selectedType,
    setSelectedType,
    typeColourInput,
    setTypeColourInput,
    shape,
    setShape,
    description,
    setDescription,
    savePOI,
    saveType,
    showAllPoiLabels,
    setShowAllPoiLabels,
    onToggleFound,
    onCategoryUpdate,
    user,
    mapOwnerId,
    isEmbed,
    hideSidebarHover,
    setHideSidebarHover,
    commentsOpen,
    commentsWidth,
    poiName,
    setPoiName,
    visibleZoneIds,
    onZonesChanged,
    hoveredZoneId,
    pathControls = {},
    extraRegionMaps = [],
    parentMapId = null,
    parentMapName = 'Parent Map',
    currentMapId,
    onSeoEditClick,
    onOverlayStateChange,
    adsEnabled: adsEnabledProp,
  } = props;

  const adsEnabled = (() => {
    if (typeof adsEnabledProp === 'boolean') return adsEnabledProp;

    const v =
      mapData?.ads_enabled ??
      mapData?.adsEnabled ??
      mapData?.show_ads ??
      mapData?.showAds ??
      mapData?.allow_ads ??
      mapData?.allowAds ??
      mapData?.ads ??
      mapData?.enable_ads ??
      mapData?.enableAds;

    if (v === true) return true;
    if (v === false) return false;
    if (v === 1) return true;
    if (v === 0) return false;

    const s = (v == null ? '' : String(v)).toLowerCase().trim();
    if (s === '1' || s === 'true' || s === 'yes' || s === 'on') return true;
    if (s === '0' || s === 'false' || s === 'no' || s === 'off') return false;

    return false;
  })();

  const MOBILE_AD_W = 320;
  const MOBILE_AD_H = 50;
  const MOBILE_AD_PAD_Y = 6;
  const MOBILE_AD_BAR_H = MOBILE_AD_H + MOBILE_AD_PAD_Y * 2;

  const rememberUiKey = 'pa_remember_map_ui';
  const [rememberUiState, setRememberUiState] = useState(true);
  const lastRememberUserIdRef = useRef(null);

  const isLoggedIn = !!(user && user.id);
  const userId = user && user.id ? String(user.id) : null;

  useEffect(() => {
    let cancelled = false;

    const initRememberFromDbOrLocal = async () => {
      if (isLoggedIn) {
        if (lastRememberUserIdRef.current === userId) return;
        lastRememberUserIdRef.current = userId;

        try {
          const res = await fetch('/api/user/settings', { credentials: 'include' });
          const data = await res.json().catch(() => null);
          if (cancelled) return;

          if (data && typeof data === 'object' && data.remember_map_prefs != null) {
            const v =
              data.remember_map_prefs === 1 ||
              data.remember_map_prefs === true ||
              data.remember_map_prefs === '1';
            setRememberUiState(!!v);
            return;
          }
        } catch {
        }

        setRememberUiState(true);
        return;
      }

      lastRememberUserIdRef.current = null;

      try {
        const v = localStorage.getItem(rememberUiKey);
        if (v === 'false') setRememberUiState(false);
        if (v === 'true') setRememberUiState(true);
      } catch {}
    };

    initRememberFromDbOrLocal();

    return () => {
      cancelled = true;
    };
  }, [isLoggedIn, userId]);

  useEffect(() => {
    if (isLoggedIn) return;
    try {
      localStorage.setItem(rememberUiKey, rememberUiState ? 'true' : 'false');
    } catch {}
  }, [rememberUiState, isLoggedIn]);

  const toggleRememberUiState = async () => {
    const next = !rememberUiState;
    setRememberUiState(next);

    if (!isLoggedIn) {
      try {
        localStorage.setItem(rememberUiKey, next ? 'true' : 'false');
      } catch {}
      return;
    }

    try {
      await fetch('/api/user/settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ remember_map_prefs: next ? 1 : 0 }),
      });
    } catch {
    }
  };

  const wipePreferences = async () => {
    const mapIdStr = mapData?.id != null ? String(mapData.id) : '';

    const perMapKeys = [
      mapIdStr ? `personalPins_${mapIdStr}` : null,
      mapIdStr ? `foundPOIs_${mapIdStr}` : null,
      mapIdStr ? `foundPois_${mapIdStr}` : null,
      mapIdStr ? `poiLabelScale_${mapIdStr}` : null,
      mapIdStr ? `sidebarExpansion_${mapIdStr}` : null,
      mapIdStr ? `sidebarExpansionState_${mapIdStr}` : null,
      mapIdStr ? `pa_sidebar_state_${mapIdStr}` : null,
      mapIdStr ? `pa_map_ui_state_${mapIdStr}` : null,
    ].filter(Boolean);

    const globalKeys = [
      'mapHelpDrawer_open',
      'sidebar_open',
      'sidebar_pois_open',
      'sidebar_zones_open',
      'sidebar_paths_open',
      'sidebar_regionmaps_open',
      'pa_sidebar_state',
      'pa_map_ui_state',
      'poiLabelScale',
    ];

    try {
      [...perMapKeys, ...globalKeys].forEach((k) => {
        try {
          localStorage.removeItem(k);
        } catch {}
      });
    } catch {}

    try {
      setRememberUiState(true);
      if (!isLoggedIn) {
        try {
          localStorage.setItem(rememberUiKey, 'true');
        } catch {}
      } else {
        try {
          await fetch('/api/user/settings', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ remember_map_prefs: 1 }),
          });
        } catch {}
      }
    } catch {}

    try {
      window.location.reload();
    } catch {}
  };

  const [personalPins, setPersonalPins] = useState([]);
  const [pinsLoaded, setPinsLoaded] = useState(false);
  const [placingPin, setPlacingPin] = useState(false);
  const [editPinIdx, setEditPinIdx] = useState(null);
  const [modalPin, setModalPin] = useState(null);
  const [showEmbedPopup, setShowEmbedPopup] = useState(false);
  const [localTypeColour, setLocalTypeColour] = useState(typeColourInput);
  const [colourLocked, setColourLocked] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [selectedPoiId, setSelectedPoiId] = useState(null);
  const [selectedShape, setSelectedShape] = useState(null);
  const [selectedLayer, setSelectedLayer] = useState(null);
  const [savingShape, setSavingShape] = useState(false);
  const [isGeomanActive, setIsGeomanActive] = useState(false);

  const [iconFile, setIconFile] = useState(null);
  const [iconPreview, setIconPreview] = useState(null);
  const fileInputRef = useRef();

  const [localLabelBorder, setLocalLabelBorder] = useState(1);
  const [localLabelBorderColour, setLocalLabelBorderColour] = useState('#000000');
  const [poiImageFile, setPoiImageFile] = useState(null);
  const [poiImagePreview, setPoiImagePreview] = useState(null);
  const poiImageInputRef = useRef();

  const shapeLayerMap = useRef({});
  const prevOriginalStyles = useRef({});
  const animationRef = useRef(null);
  const glowOnRef = useRef(true);

  const [pathingEntries, setPathingEntries] = useState([]);
  const [lastCreatedPoiContext, setLastCreatedPoiContext] = useState(null);

  const [forceHideCreate, setForceHideCreate] = useState(false);
  useEffect(() => {
    setForceHideCreate(false);
  }, [pendingPOI]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') setForceHideCreate(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const [poiInfoOpen, setPoiInfoOpen] = useState(false);
  const [poiEditOpen, setPoiEditOpen] = useState(false);

  const otherModalOpen = useAnyModalOpen(['poi-create-modal-root']);
  const [canCreateHere, setCanCreateHere] = useState(true);
  const canCreateHereRef = useRef(true);

  const mapDivRef = useRef();

  const normalizedHoveredZoneId =
    hoveredZoneId != null ? Number(hoveredZoneId) : null;

  useZoneHoverGlow(
    normalizedHoveredZoneId,
    shapeLayerMap,
    prevOriginalStyles,
    animationRef,
    glowOnRef
  );

  useMapCreateGuard(
    mapDivRef,
    hoveredPoiId,
    setHoveredPoiId,
    canCreateHereRef,
    setCanCreateHere
  );

  const {
    selectedOverlayAsset,
    setSelectedOverlayAsset,
    overlayBounds,
    setOverlayBounds,
    overlaySaving,
    overlayError,
    overlayEditMode,
    overlayEditLocked,
    overlayImageUrl,
    overlayAssets,
    handleStartOverlayEdit,
    handleCancelOverlayEdit,
    saveOverlayPlacement,
  } = useOverlayEditing({ mapData, imageSize });

  const [overlayUiActive, setOverlayUiActive] = useState(false);

  const [overlayAssetsView, setOverlayAssetsView] = useState(overlayAssets || []);

  useEffect(() => {
    setOverlayAssetsView(overlayAssets || []);
  }, [overlayAssets]);

  useEffect(() => {
    if (typeof onOverlayStateChange === 'function') {
      onOverlayStateChange({
        activeOverlayId: selectedOverlayAsset ? selectedOverlayAsset.id : null,
        hasActiveOverlay:
          !!(selectedOverlayAsset && overlayBounds && overlayImageUrl),
      });
    }
  }, [selectedOverlayAsset, overlayBounds, overlayImageUrl, onOverlayStateChange]);

  const handleSelectOverlay = (asset) => {
    if (asset) {
      setSelectedOverlayAsset(asset);
      setOverlayBounds(assetToBounds(asset, imageSize));
      if (typeof onOverlayStateChange === 'function') {
        onOverlayStateChange({
          activeOverlayId: asset.id ?? null,
          hasActiveOverlay: true,
        });
      }
    } else {
      setSelectedOverlayAsset(null);
      setOverlayBounds(null);
      if (typeof onOverlayStateChange === 'function') {
        onOverlayStateChange({
          activeOverlayId: null,
          hasActiveOverlay: false,
        });
      }
    }
  };

  const handleOverlayDeleted = (asset) => {
    if (!asset || asset.id == null) return;
    const deletedId = String(asset.id);

    setOverlayAssetsView((prev) =>
      Array.isArray(prev)
        ? prev.filter((a) => String(a.id) !== deletedId)
        : prev
    );

    if (selectedOverlayAsset && String(selectedOverlayAsset.id) === deletedId) {
      setSelectedOverlayAsset(null);
      setOverlayBounds(null);
      if (typeof onOverlayStateChange === 'function') {
        onOverlayStateChange({
          activeOverlayId: null,
          hasActiveOverlay: false,
        });
      }
    }
  };

  const {
    handleGeomanButtonClick,
    handleSaveShape,
    handleDeleteShape,
    handleShapeToolbarChange,
    openShapeToolbar,
  } = createShapeHandlers({
    apiBase,
    isMapOwnerOrFounder,
    selectedShape,
    selectedLayer,
    setSelectedShape,
    setSelectedLayer,
    setSavingShape,
    setPathingEntries,
    onZonesChanged,
    shapeLayerMap,
  });

  const {
    deleteAllPins,
    handleMapClick,
    handlePinMarkerClick,
    handleModalSave,
    handleModalDelete,
    handleModalCancel,
  } = createPersonalPinHandlers({
    placingPin,
    setPlacingPin,
    personalPins,
    setPersonalPins,
    modalPin,
    setModalPin,
    editPinIdx,
    setEditPinIdx,
  });

  // FIX: forward ALL args so POICreationPopup's extra payload (overlayId, colour, stylePayload) isn't dropped.
  const instrumentedSavePOI = (...args) => {
    const name = args[0];
    const imgUrl = args[1];
    const desc = args[2];

    if (pendingPOI && selectedType) {
      setLastCreatedPoiContext({
        x: pendingPOI.x,
        y: pendingPOI.y,
        type: selectedType,
        category: selectedCategory || null,
        colour: localTypeColour,
        createdAt: Date.now(),
      });
    }

    return savePOI(...args);
  };

  const { closeCreateModal, savePOIAndClose } = createPoiCreationHandlers({
    fileInputRef,
    poiImageInputRef,
    setIconFile,
    setIconPreview,
    setPoiImageFile,
    setPoiImagePreview,
    setForceHideCreate,
    setPendingPOI,
    savePOI: instrumentedSavePOI,
  });

  const [isMobile, setIsMobile] = useState(isMobileScreen());
  const [toolboxBottom, setToolboxBottom] = useState(98);

  useEffect(() => {
    const handler = () => setIsMobile(isMobileScreen());
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const showMobileBottomAd = !!(isMobile && !isEmbed && adsEnabled);

  useEffect(() => {
    if (typeof document === 'undefined') return;

    const root = document.documentElement;
    if (showMobileBottomAd) {
      root.style.setProperty('--pa-mobile-bottom-ad-h', `${MOBILE_AD_BAR_H}px`);
    } else {
      root.style.setProperty('--pa-mobile-bottom-ad-h', '0px');
    }

    return () => {
      try {
        root.style.setProperty('--pa-mobile-bottom-ad-h', '0px');
      } catch {}
    };
  }, [showMobileBottomAd]);

  useEffect(() => {
    if (!isMobile) {
      setToolboxBottom(98);
      return;
    }
    function updateToolboxBottom() {
      const bar = document.getElementById('mobile-tab-bar');
      if (bar) {
        const rect = bar.getBoundingClientRect();
        const windowHeight =
          window.innerHeight || document.documentElement.clientHeight;
        const offset = Math.max(windowHeight - rect.top, 0);
        setToolboxBottom(offset + 5);
      } else {
        setToolboxBottom(25);
      }
    }
    updateToolboxBottom();
    window.addEventListener('resize', updateToolboxBottom);
    return () => window.removeEventListener('resize', updateToolboxBottom);
  }, [isMobile]);

  const [resetTrigger, setResetTrigger] = useState(0);

  const { handleUnlockColour, handleResetView } = createViewHandlers({
    setColourLocked,
    setResetTrigger,
  });

  const [showOverlayModal, setShowOverlayModal] = useState(false);

  useEffect(() => {
    if (!selectedPoiId) return;
    const stillVisible = pois.find(
      poi =>
        poi.id === selectedPoiId &&
        poi.category &&
        poi.type &&
        visibleTypes.has(poi.type) &&
        visibleCategories.has(poi.category)
    );
    if (!stillVisible) setSelectedPoiId(null);
  }, [pois, selectedPoiId, visibleTypes, visibleCategories]);

  useEffect(() => {
    if (!selectedType) {
      setLocalTypeColour('#4285f4');
      setColourLocked(false);
      return;
    }
    const existing = pois.find(
      p => p.type === selectedType && typeColours[selectedType]
    );
    if (existing) {
      setLocalTypeColour(typeColours[selectedType]);
      setColourLocked(true);
    } else {
      setLocalTypeColour('#4285f4');
      setColourLocked(false);
    }
  }, [selectedType, pois, typeColours]);

  useEffect(() => {
    if (typeof setTypeColourInput === 'function') {
      setTypeColourInput(localTypeColour || '#ff0000');
    }
  }, [localTypeColour, setTypeColourInput]);

  usePersonalPinsStorage(
    mapData?.id,
    personalPins,
    setPersonalPins,
    pinsLoaded,
    setPinsLoaded
  );

  const [mapStyles, setMapStyles] = useState(null);

  const fetchMapStyles = async (targetMapId) => {
    if (!targetMapId) return;
    try {
      const res = await fetch(`/api/map-styles/${targetMapId}`, { credentials: 'include' });
      const data = await res.json().catch(() => null);
      if (data && typeof data === 'object') {
        setMapStyles(data);
      } else {
        setMapStyles(null);
      }
    } catch {
      setMapStyles(null);
    }
  };

  useEffect(() => {
    const mapId = mapData?.id;
    if (!mapId) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/map-styles/${mapId}`, { credentials: 'include' });
        const data = await res.json().catch(() => null);
        if (cancelled) return;
        if (data && typeof data === 'object') {
          setMapStyles(data);
        } else {
          setMapStyles(null);
        }
      } catch {
        if (!cancelled) setMapStyles(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [mapData?.id]);

  useEffect(() => {
    const mapId = mapData?.id;
    if (!mapId) return;

    const handler = (e) => {
      const detail = e?.detail || {};
      if (detail.mapId && String(detail.mapId) !== String(mapId)) return;
      fetchMapStyles(mapId);
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('pa-map-styles-updated', handler);
      return () => window.removeEventListener('pa-map-styles-updated', handler);
    }
    return undefined;
  }, [mapData?.id]);

  if (!imageSize || !mapData) {
    return (
      <div
        style={{
          color: '#fff',
          fontSize: 18,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
        }}
      >
        Loading map...
      </div>
    );
  }

  const pinPanelStyle = {
    position: 'fixed',
    right: commentsOpen ? commentsWidth + 30 : 26,
    bottom: 98,
    background: '#23242a',
    borderRadius: '18px 18px 10px 10px',
    boxShadow: '0 2px 12px #000b',
    color: '#ffe670',
    zIndex: 1550,
    minWidth: 200,
    maxWidth: 260,
    maxHeight: 300,
    padding: '12px 10px 7px 16px',
    border: '2px solid #393b49',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 2,
    overflowY: 'auto',
    transition: 'right 0.25s cubic-bezier(0.47, 1.64, 0.41, 0.8)',
  };

  const mapKey =
    mapData && imageSize
      ? `${mapData.id}_${imageSize[0]}x${imageSize[1]}`
      : 'no-map';

  const categoryId = (() => {
    if (!selectedCategory) return null;
    const match = Array.isArray(customCategories)
      ? customCategories.find(
          c =>
            (c?.name || '').toString() ===
            selectedCategory.toString()
        )
      : null;
    return match?.id ?? null;
  })();

  const safePathControls = pathControls || {};

  const hasActiveOverlay =
    !!(selectedOverlayAsset && overlayBounds && overlayImageUrl);

  const filteredPois = Array.isArray(pois)
    ? pois.filter((poi) => {
        let overlayId =
          poi.overlay_id != null
            ? poi.overlay_id
            : poi.overlayId != null
            ? poi.overlayId
            : null;

        if (overlayId === '' || overlayId === undefined) {
          overlayId = null;
        }

        if (overlayId === null) return true;

        const numericOverlayId = Number(overlayId);
        if (!Number.isFinite(numericOverlayId)) {
          return true;
        }

        if (numericOverlayId === 1) {
          return !hasActiveOverlay;
        }

        if (!hasActiveOverlay) return false;

        return (
          selectedOverlayAsset &&
          String(selectedOverlayAsset.id) === String(numericOverlayId)
        );
      })
    : [];

  const now = Date.now();
  const enhancedPois = Array.isArray(filteredPois)
    ? filteredPois.map((poi) => {
        let patchedPoi = poi;
        if (
          lastCreatedPoiContext &&
          typeof poi?.x === 'number' &&
          typeof poi?.y === 'number'
        ) {
          const age = now - lastCreatedPoiContext.createdAt;
          if (
            age >= 0 &&
            age <= 15000 &&
            Math.round(poi.x) === Math.round(lastCreatedPoiContext.x) &&
            Math.round(poi.y) === Math.round(lastCreatedPoiContext.y)
          ) {
            patchedPoi = {
              ...poi,
              colour: lastCreatedPoiContext.colour,
              color: lastCreatedPoiContext.colour,
            };
          }
        }
        return patchedPoi;
      })
    : [];

  const overlayMenuTitle = mapStyles?.overlay_menu_title;
  const overlayMenuSize = mapStyles?.overlay_menu_size;
  const overlayMenuBaseLabel = mapStyles?.overlay_menu_base_label;

  return (
    <POISelectedIdContext.Provider value={[selectedPoiId, setSelectedPoiId]}>
      <div
        ref={mapDivRef}
        style={{
          flex: '1 1 0%',
          minHeight: 0,
          minWidth: 0,
          position: 'relative',
        }}
      >
        {showMobileBottomAd && (
          <style>{`
            #mobile-tab-bar {
              bottom: calc(var(--pa-mobile-bottom-ad-h, 0px) + env(safe-area-inset-bottom, 0px)) !important;
            }
          `}</style>
        )}

        {showMobileBottomAd && (
          <div
            id="pa-mobile-bottom-ad"
            style={{
              position: 'fixed',
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1700,
              background: 'linear-gradient(90deg, #222 90%, #444)',
              boxShadow: '0 -2px 14px #0009',
              paddingTop: MOBILE_AD_PAD_Y,
              paddingBottom: `calc(${MOBILE_AD_PAD_Y}px + env(safe-area-inset-bottom, 0px))`,
              paddingLeft: 8,
              paddingRight: 8,
              boxSizing: 'border-box',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <div
              style={{
                width: '100%',
                maxWidth: 420,
                height: MOBILE_AD_H,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <AdSlot
                id="Map_mobile_bottom_banner"
                sizes={[[MOBILE_AD_W, MOBILE_AD_H]]}
                refreshTime={30}
                refreshVisibleOnly={true}
                style={{
                  display: 'block',
                  width: '100%',
                  height: MOBILE_AD_H,
                  minHeight: MOBILE_AD_H,
                }}
                targeting={{
                  placement: 'map',
                  pos: 'mobile_bottom',
                }}
              />
            </div>
          </div>
        )}

        {selectedShape && isMapOwnerOrFounder && (
          <ShapeEditToolbar
            shape={selectedShape}
            layer={selectedLayer}
            onChange={handleShapeToolbarChange}
            onSave={handleSaveShape}
            onClose={() => {
              setSelectedShape(null);
              setSelectedLayer(null);
            }}
            onDelete={handleDeleteShape}
            saving={savingShape}
            canEdit={isMapOwnerOrFounder}
            extraRegionMaps={extraRegionMaps}
            parentMapId={parentMapId}
            parentMapName={parentMapName}
            currentMapId={currentMapId || mapData.id}
            overlayAssets={overlayAssetsView}
            activeOverlayId={selectedOverlayAsset ? selectedOverlayAsset.id : null}
          />
        )}

        {overlayEditMode && selectedOverlayAsset && overlayBounds && (
          <div
            style={{
              position: 'fixed',
              left: '50%',
              transform: 'translateX(-50%)',
              bottom: isMobile ? toolboxBottom + 60 : 110,
              zIndex: 2000,
              background: 'rgba(10,12,22,0.96)',
              borderRadius: 999,
              border: '1px solid rgba(80,90,130,0.9)',
              boxShadow: '0 8px 20px rgba(0,0,0,0.7)',
              padding: '8px 14px',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              fontSize: 13,
              color: '#f4f5ff',
            }}
          >
            <span>
              Editing overlay placement for{' '}
              <strong>
                {selectedOverlayAsset.title ||
                  selectedOverlayAsset.name ||
                  'overlay'}
              </strong>
            </span>
            {overlayError && (
              <span style={{ color: '#ff7b7b', marginLeft: 8 }}>
                {overlayError}
              </span>
            )}
            <button
              type="button"
              onClick={handleCancelOverlayEdit}
              style={{
                padding: '4px 10px',
                borderRadius: 999,
                border: '1px solid #3a3d52',
                background: '#191b26',
                color: '#d0d3ea',
                cursor: 'pointer',
                fontWeight: 500,
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={saveOverlayPlacement}
              disabled={overlaySaving}
              style={{
                padding: '4px 14px',
                borderRadius: 999,
                border: 'none',
                background: overlaySaving
                  ? 'linear-gradient(135deg, #3f4d82, #58648f)'
                  : 'linear-gradient(135deg, #3c6df6, #5f9dfd)',
                color: '#fff',
                cursor: overlaySaving ? 'default' : 'pointer',
                fontWeight: 600,
              }}
            >
              {overlaySaving ? 'Saving…' : 'Save placement'}
            </button>
          </div>
        )}

        {!isEmbed && (
          <MapControls
            isMobile={isMobile}
            toolboxBottom={toolboxBottom}
            showFound={showFound}
            setShowFound={setShowFound}
            placingPin={placingPin}
            setPlacingPin={setPlacingPin}
            isMapOwnerOrFounder={isMapOwnerOrFounder}
            onSeoEditClick={onSeoEditClick}
            onShowOverlayModal={
              isMapOwnerOrFounder ? () => setShowOverlayModal(true) : undefined
            }
            onShowEmbedPopup={() => setShowEmbedPopup(true)}
            onResetView={handleResetView}
            adsEnabled={adsEnabled}
            rememberUiState={rememberUiState}
            onToggleRememberUiState={toggleRememberUiState}
            onWipePreferences={wipePreferences}
          />
        )}

        {!isEmbed && !isMobile && personalPins.length > 0 && (
          <div style={pinPanelStyle} className="personal-pin-modal">
            <div
              style={{
                fontWeight: 700,
                fontSize: 15,
                marginBottom: 6,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <span>Your Pins ({personalPins.length})</span>
              <button
                onClick={deleteAllPins}
                style={{
                  marginLeft: 'auto',
                  background: '#f44c2b',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 7,
                  padding: '2px 10px',
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: 'pointer',
                }}
                title="Delete All"
              >
                ×
              </button>
            </div>
            <ul
              style={{
                listStyle: 'none',
                padding: 0,
                margin: 0,
                width: '100%',
              }}
            >
              {personalPins.map((pin, idx) => (
                <li
                  key={idx}
                  style={{
                    marginBottom: 3,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 7,
                    minHeight: 28,
                  }}
                >
                  <span
                    style={{
                      width: 18,
                      height: 18,
                      display: 'inline-block',
                      borderRadius: '50%',
                      background: pin.colour,
                      border: '2px solid #333',
                      flexShrink: 0,
                    }}
                  />
                  <span
                    style={{
                      fontWeight: 500,
                      fontFamily: 'monospace',
                      color: '#fff',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      maxWidth: 102,
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      fontSize: 15,
                      lineHeight: '18px',
                    }}
                    onClick={() => handlePinMarkerClick(idx)}
                    title={pin.label || `Pin ${idx + 1}`}
                  >
                    {pin.label ? pin.label : `Pin ${idx + 1}`}
                  </span>
                  <button
                    onClick={() => handlePinMarkerClick(idx)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#ffe670',
                      cursor: 'pointer',
                      fontSize: 15,
                      padding: 0,
                    }}
                    title="Edit"
                  >
                    ✎
                  </button>
                  <button
                    onClick={() => {
                      setEditPinIdx(idx);
                      setModalPin(pin);
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#f45e19',
                      cursor: 'pointer',
                      fontSize: 15,
                      padding: 0,
                      marginLeft: 3,
                    }}
                    title="Delete"
                  >
                    🗑
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {modalPin && (
          <PersonalPinModal
            mode={editPinIdx === null ? 'add' : 'edit'}
            initial={modalPin}
            onSave={handleModalSave}
            onDelete={handleModalDelete}
            onCancel={handleModalCancel}
          />
        )}

        {!isEmbed && showEmbedPopup && (
          <EmbedPopup
            mapId={mapData.id}
            mapName={mapData.name || 'Untitled Map'}
            onClose={() => setShowEmbedPopup(false)}
          />
        )}

        {!isEmbed && showOverlayModal && (
          <OverlayAssetModal
            mapId={mapData.id}
            initialSelectedAsset={selectedOverlayAsset}
            onClose={() => setShowOverlayModal(false)}
            onSelectAsset={(asset) => {
              handleSelectOverlay(asset);
              setShowOverlayModal(false);
            }}
            onStartEdit={(asset, locked) => {
              handleStartOverlayEdit(asset, locked);
            }}
            onDeleteAsset={handleOverlayDeleted}
          />
        )}

        <MapContainer
          key={mapKey}
          crs={L.CRS.Simple}
          bounds={[[0, 0], imageSize]}
          style={{ height: '100%', width: '100%' }}
          minZoom={-4}
          maxZoom={4}
          zoom={0}
          zoomSnap={0.1}
          zoomDelta={0.5}
          scrollWheelZoom
          zoomControl={!isMobile}
        >
          <FitToImage imageSize={imageSize} mapId={mapData.id} resetTrigger={resetTrigger} />
          <AllowLoosePanning />

          <SetZoomControlPosition />
          <MouseCoords onHover={handleMouseCoord} />

          {placingPin && (
            <ClickHandler
              onClick={handleMapClick}
              enabled={!isGeomanActive && !overlayEditMode && !overlayUiActive}
            />
          )}
          {isMapOwnerOrFounder && !placingPin && (
            <ClickHandler
              onClick={handleClick}
              enabled={
                !isGeomanActive &&
                !overlayEditMode &&
                !overlayUiActive &&
                canCreateHere &&
                !otherModalOpen &&
                !(
                  modalPin ||
                  showEmbedPopup ||
                  selectedShape ||
                  poiInfoOpen ||
                  poiEditOpen
                )
              }
            />
          )}
          {!isMapOwnerOrFounder && !placingPin && (
            <ClickHandler
              onClick={handleMapCoordClick}
              enabled={
                !isGeomanActive &&
                !overlayEditMode &&
                !overlayUiActive &&
                canCreateHere &&
                !otherModalOpen &&
                !(
                  modalPin ||
                  showEmbedPopup ||
                  selectedShape ||
                  poiInfoOpen ||
                  poiEditOpen
                )
              }
            />
          )}
          <MapClickPopupCloser />

          <GeomanControls
            mapId={mapData.id}
            userId={user?.id}
            hoveredZoneId={normalizedHoveredZoneId}
            onShapesChanged={() => {}}
            onZonesChanged={onZonesChanged}
            onShapeClick={isMapOwnerOrFounder ? openShapeToolbar : undefined}
            shapeLayerMap={shapeLayerMap}
            onButtonClick={handleGeomanButtonClick}
            visibleZoneIds={visibleZoneIds}
            canEdit={isMapOwnerOrFounder}
            onPathingEntriesChange={setPathingEntries}
            onGeomanActiveChange={setIsGeomanActive}
          />

          <ImageOverlay
            url={resolveUploadsUrl(mapData.image_url)}
            bounds={[[0, 0], imageSize]}
            zIndex={100}
          />

          {selectedOverlayAsset && overlayBounds && overlayImageUrl && (
            <ImageOverlay
              url={overlayImageUrl}
              bounds={overlayBounds}
              opacity={
                Number.isFinite(Number(selectedOverlayAsset.opacity))
                  ? Number(selectedOverlayAsset.opacity)
                  : 1
              }
              zIndex={
                Number.isFinite(Number(selectedOverlayAsset.z_index))
                  ? 101 + Number(selectedOverlayAsset.z_index)
                  : 130
              }
            />
          )}

          <POILayer
            pois={enhancedPois}
            foundPOIs={foundPOIs}
            showFound={showFound}
            customCategories={customCategories}
            customTypes={Array.isArray(customTypes) ? customTypes : []}
            hoveredPoiId={hoveredPoiId}
            hoveredType={hoveredType}
            setHoveredPoiId={setHoveredPoiId}
            visibleTypes={visibleTypes}
            visibleCategories={visibleCategories}
            showAllPoiLabels={showAllPoiLabels}
            typeColours={typeColours}
            onCategoryUpdate={onCategoryUpdate}
            isMapOwnerOrFounder={isMapOwnerOrFounder}
            mapId={mapData.id}
            user={user}
            mapOwnerId={mapOwnerId}
            onToggleFound={onToggleFound}
            selectedPoiId={selectedPoiId}
            setSelectedPoiId={setSelectedPoiId}
            hideSidebarHover={hideSidebarHover}
            onOpenInfoModal={() => setPoiInfoOpen(true)}
            onCloseInfoModal={() => setPoiInfoOpen(false)}
            onOpenEditModal={() => setPoiEditOpen(true)}
            onCloseEditModal={() => setPoiEditOpen(false)}
            overlayOptions={overlayAssetsView}
            activeOverlayId={selectedOverlayAsset ? selectedOverlayAsset.id : null}
          />

          {personalPins.map((pin, idx) => (
            <Marker
              key={`personal-pin-${idx}`}
              position={[pin.y, pin.x]}
              icon={personalPinIcon(pin.colour)}
              eventHandlers={{
                click: () => {
                  handlePinMarkerClick(idx);
                },
              }}
            >
              {pin.label && pin.label.trim() && (
                <Tooltip
                  direction="bottom"
                  offset={[0, 12]}
                  opacity={1}
                  className="personal-pin-label"
                >
                  {pin.label}
                </Tooltip>
              )}
            </Marker>
          ))}

          {pathingEntries.map(({ id, shape: s, layer }) => (
            <ShapePathingRunner
              key={id}
              shape={s}
              layer={layer}
              enabled
              controls={safePathControls}
              isHovered={normalizedHoveredZoneId != null && Number(id) === normalizedHoveredZoneId}
              showPoiLabels={showAllPoiLabels}
            />
          ))}

          <OverlayEditHUD
            bounds={overlayBounds}
            setBounds={setOverlayBounds}
            locked={overlayEditLocked}
            editing={overlayEditMode}
            overlays={overlayAssetsView}
            selectedOverlay={selectedOverlayAsset}
            onSelectOverlay={handleSelectOverlay}
            onUiActiveChange={setOverlayUiActive}
            menuTitle={overlayMenuTitle}
            menuSize={overlayMenuSize}
            baseLabel={overlayMenuBaseLabel}
          />
        </MapContainer>

        <div
          id="poi-create-modal-root"
          className="poi-creation-popup"
        >
          <POICreationPopup
            show={
              !!(pendingPOI && isMapOwnerOrFounder) &&
              !overlayEditMode &&
              !forceHideCreate &&
              !otherModalOpen &&
              !(poiInfoOpen || poiEditOpen)
            }
            onClose={closeCreateModal}
            poiName={poiName}
            setPoiName={setPoiName}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            selectedType={selectedType}
            setSelectedType={setSelectedType}
            localTypeColour={localTypeColour}
            setLocalTypeColour={setLocalTypeColour}
            colourLocked={colourLocked}
            handleUnlockColour={handleUnlockColour}
            shape={shape}
            setShape={setShape}
            localLabelBorder={localLabelBorder}
            setLocalLabelBorder={setLocalLabelBorder}
            localLabelBorderColour={localLabelBorderColour}
            setLocalLabelBorderColour={setLocalLabelBorderColour}
            description={description}
            setDescription={setDescription}
            iconFile={iconFile}
            setIconFile={setIconFile}
            iconPreview={iconPreview}
            setIconPreview={setIconPreview}
            poiImageFile={poiImageFile}
            setPoiImageFile={setPoiImageFile}
            poiImagePreview={poiImagePreview}
            setPoiImagePreview={setPoiImagePreview}
            fileInputRef={fileInputRef}
            poiImageInputRef={poiImageInputRef}
            savePOI={savePOIAndClose}
            saveType={saveType}
            selectedTypeDisabled={!selectedType}
            customCategories={customCategories}
            customTypes={Array.isArray(customTypes) ? customTypes : []}
            imageUrl={imageUrl}
            categoryId={categoryId}
            mapId={mapData.id}
            hasActiveOverlay={!!selectedOverlayAsset}
            lockToOverlay={!!selectedOverlayAsset}
            setLockToOverlay={() => {}}
            overlayIdDefault={selectedOverlayAsset ? selectedOverlayAsset.id : null}
            overlayOptions={overlayAssetsView}
          />
        </div>

        {shareCoord && (
          <CoordPopup
            coord={shareCoord}
            onCopy={handleCopyCoord}
            copied={copiedCoord}
          />
        )}
      </div>
    </POISelectedIdContext.Provider>
  );
}

export default InteractiveMap;
